import { useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import "@/App.css";
import {
  ArrowUp,
  Award,
  Briefcase,
  Download,
  FileText,
  GraduationCap,
  Instagram,
  Languages,
  Linkedin,
  LogIn,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Megaphone,
  Menu,
  MessageCircle,
  Moon,
  Palette,
  Play,
  Presentation,
  Sun,
  X,
  Youtube,
} from "lucide-react";

const PROFILE_PHOTO =
  "https://customer-assets-lqy194kg.emergentagent.net/job_41d62a77-a131-4b89-ab2b-45f5a0b2dc95/artifacts/sq75rxah_WhatsApp%20Image%202026-09-24%20at%2020.06.06.jpeg";

const WA_NUMBER = "6282118790441";
const CV_URL = "/CV-Ardi-Septiono.pdf";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const handleGoogleSignIn = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const ASSETS = {
  kkn: "https://customer-assets-lqy194kg.emergentagent.net/job_41d62a77-a131-4b89-ab2b-45f5a0b2dc95/artifacts/xiw5y5uh_KKN%281%29.webp",
  elegant:
    "https://customer-assets-lqy194kg.emergentagent.net/job_41d62a77-a131-4b89-ab2b-45f5a0b2dc95/artifacts/tx9n5ptu_ELEGANT%281%29.png",
  hbd: "https://customer-assets-lqy194kg.emergentagent.net/job_41d62a77-a131-4b89-ab2b-45f5a0b2dc95/artifacts/pbxbl60q_HBD.png",
  founding:
    "https://customer-assets-lqy194kg.emergentagent.net/job_41d62a77-a131-4b89-ab2b-45f5a0b2dc95/artifacts/scqnz8qf_tas.png",
  recap:
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_ardi-visual-works/artifacts/izad0aza_RECAP.png",
  ppdb: "https://customer-assets-gfyr7b9c.emergentagent.net/job_ardi-visual-works/artifacts/c4ym22x2_BINGUNG%20KULIAH%20DIMANA.webp",
  puma: "https://customer-assets-gfyr7b9c.emergentagent.net/job_ardi-visual-works/artifacts/37ldsk0u_1.png",
  seiko:
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_ardi-visual-works/artifacts/b69q3per_Teks%20paragraf%20Anda.png",
  takka:
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_ardi-visual-works/artifacts/lyc26v4t_PROMO%2010.10.png",
};

const PROJECTS = [
  {
    id: "kkn",
    category: "social",
    categoryLabel: "Social Media",
    type: "image",
    src: ASSETS.kkn,
    title: {
      id: "Poster KKN Universitas Yatsi Madani",
      en: "KKN Poster — Yatsi Madani University",
    },
    description: {
      id: "Desain poster kolase untuk kegiatan KKN Universitas Yatsi Madani di Desa Pabuaran, Kota Tangerang. Menggabungkan dokumentasi kegiatan dengan tipografi tegas dan efek cahaya dinamis.",
      en: "Collage poster design for the community service program (KKN) of Yatsi Madani University in Pabuaran Village, Tangerang. Combines activity documentation with bold typography and dynamic light effects.",
    },
  },
  {
    id: "elegant",
    category: "design",
    categoryLabel: "Design",
    type: "image",
    src: ASSETS.elegant,
    title: {
      id: "Elegant Look — Fashion Magazine",
      en: "Elegant Look — Fashion Magazine",
    },
    description: {
      id: "Konsep sampul majalah fashion bernuansa Gen Z dengan palet hijau pastel, tipografi neon, dan elemen antarmuka media sosial yang interaktif.",
      en: "A Gen Z-inspired fashion magazine cover concept with a pastel green palette, neon typography, and interactive social media interface elements.",
    },
  },
  {
    id: "hbd",
    category: "social",
    categoryLabel: "Social Media",
    type: "image",
    src: ASSETS.hbd,
    title: {
      id: "Happy Birthday Visual",
      en: "Happy Birthday Visual",
    },
    description: {
      id: "Desain ucapan ulang tahun bergaya elegan dengan bingkai emas ornamen dan nuansa warna hangat, dibuat untuk konten personal di media sosial.",
      en: "An elegant birthday greeting design with an ornate golden frame and warm tones, created for personal social media content.",
    },
  },
  {
    id: "founding",
    category: "design",
    categoryLabel: "Design",
    type: "image",
    src: ASSETS.founding,
    title: {
      id: "The Founding Father — Poster Tribute",
      en: "The Founding Father — Tribute Poster",
    },
    description: {
      id: "Poster tribut untuk Bapak Proklamator Indonesia dengan komposisi bingkai emas, foto historis, dan pencahayaan dramatis bernuansa kepahlawanan.",
      en: "A tribute poster for Indonesia's founding father, composed with golden frames, historical photos, and dramatic heroic lighting.",
    },
  },
  {
    id: "recap",
    category: "social",
    categoryLabel: "Social Media",
    type: "image",
    src: ASSETS.recap,
    title: {
      id: "RECAP KKN — Kolase Dokumentasi",
      en: "KKN RECAP — Documentation Collage",
    },
    description: {
      id: "Desain recap kegiatan KKN bergaya papan memo dengan foto polaroid, pin merah, dan tipografi bold yang energik untuk konten media sosial.",
      en: "A KKN activity recap design in a memo-board style with polaroid photos, red pins, and energetic bold typography for social media content.",
    },
  },
  {
    id: "ppdb",
    category: "design",
    categoryLabel: "Design",
    type: "image",
    src: ASSETS.ppdb,
    title: {
      id: "Poster PPDB Universitas Yatsi Madani",
      en: "Admission Poster — Yatsi Madani University",
    },
    description: {
      id: "Poster promosi penerimaan mahasiswa baru (PPDB) tahun ajaran 2026/2027 dengan informasi jadwal gelombang pendaftaran dan pilihan ekstrakurikuler.",
      en: "A promotional poster for the 2026/2027 new student admission (PPDB), featuring registration wave schedules and extracurricular options.",
    },
  },
  {
    id: "puma",
    category: "social",
    categoryLabel: "Social Media",
    type: "image",
    src: ASSETS.puma,
    title: {
      id: "Poster Puma Speedcat",
      en: "Puma Speedcat Poster",
    },
    description: {
      id: "Poster promosi sepatu Puma Speedcat dengan tagline 'Make Every Step Count' — nuansa merah energik untuk konten produk di media sosial.",
      en: "A promo poster for Puma Speedcat shoes featuring the tagline 'Make Every Step Count' — bold red visuals for social media product content.",
    },
  },
  {
    id: "seiko",
    category: "design",
    categoryLabel: "Design",
    type: "image",
    src: ASSETS.seiko,
    title: {
      id: "Poster Seiko Premium Watch",
      en: "Seiko Premium Watch Poster",
    },
    description: {
      id: "Poster promosi jam tangan Seiko Premium bernuansa emas mewah dengan detail fitur produk (Japan Movement, Stainless Steel, Water Resistant) dan tipografi elegan.",
      en: "A luxurious gold-toned Seiko Premium Watch promo poster with product feature details (Japan Movement, Stainless Steel, Water Resistant) and elegant typography.",
    },
  },
  {
    id: "takka",
    category: "social",
    categoryLabel: "Social Media",
    type: "image",
    src: ASSETS.takka,
    title: {
      id: "Poster Promo 10.10 Takka Sosial Area",
      en: "Takka Sosial Area 10.10 Promo Poster",
    },
    description: {
      id: "Desain promo minuman 10.10 untuk Takka Sosial Area dengan tipografi bold, badge harga mulai 10K, dan ketersediaan di GrabFood & GoFood.",
      en: "A 10.10 drink promo design for Takka Sosial Area with bold typography, a 'from 10K' price badge, and GrabFood & GoFood availability.",
    },
  },
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "document", label: "Document" },
  { key: "social", label: "Social Media" },
  { key: "design", label: "Design" },
];

const NAV_LINKS = [
  { href: "#about", label: "About" },
  { href: "#skills", label: "Skills" },
  { href: "#services", label: "Services" },
  { href: "#portfolio", label: "Portfolio" },
  { href: "#member", label: "Member" },
  { href: "#experience", label: "Experience" },
  { href: "#contact", label: "Contact" },
];

const SOCIALS = [
  {
    name: "Instagram",
    href: "https://www.instagram.com/xtykaixx?stkn=M3plZmNrbDVpNW5h",
    Icon: Instagram,
  },
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/in/ardi-septiono?utm_source=share_via&utm_content=profile&utm_medium=member_android",
    Icon: Linkedin,
  },
  {
    name: "TikTok",
    href: "https://www.tiktok.com/@s.c.kai?_r=1&_t=ZS-9A0RWJo2KQw",
    Icon: Megaphone,
  },
  {
    name: "YouTube",
    href: "https://youtube.com/@yourxkiluu__?si=St8rXhYScu951RNS",
    Icon: Youtube,
  },
];

const CONTENT = {
  id: {
    themeLabel: "Ganti tema",
    menuLabel: "Buka menu",
    signIn: "Masuk",
    memberEyebrow: "Area Khusus",
    memberTitle: "Member Area",
    memberLead: "Katalog lengkap file mentah desain dalam resolusi penuh — khusus untuk pengguna yang sudah masuk.",
    memberLockedTitle: "Khusus Member",
    memberLockedText: "Masuk dengan akun Google Anda untuk mengakses dan mengunduh seluruh file desain resolusi penuh.",
    memberDownload: "Unduh",
    heroGreeting: "Hi, I'm Ardi",
    typing: ["Graphic Designer", "Mahasiswa Kewirausahaan"],
    heroDesc:
      "Sebagai seorang mahasiswa kewirausahaan yang kreatif dan berpengalaman, saya memiliki kemampuan dalam menciptakan berbagai desain visual untuk kebutuhan digital maupun cetak, seperti branding, media sosial, presentasi, poster, dan konten promosi. Saya berfokus pada desain yang menarik, komunikatif, dan sesuai dengan identitas brand, serta selalu mengikuti perkembangan tren visual modern.",
    viewWork: "View My Work",
    contactMe: "Contact Me",
    downloadCv: "Download CV",
    aboutEyebrow: "Tentang Saya",
    aboutTitle: "About Me",
    aboutDesc:
      "Saya adalah mahasiswa yang memiliki pengalaman menjadi helper tukang dan driver ShopeeFood. Selain itu, saya juga bisa mendesain di Canva. Pengalaman kerja lapangan mengajarkan saya kedisiplinan, tanggung jawab, dan komunikasi — sementara desain menjadi ruang saya untuk berkreasi dan membantu brand tampil menarik.",
    education: "Mahasiswa Universitas Yatsi Madani",
    skillsEyebrow: "Keahlian",
    skillsTitle: "Skills",
    skills: [
      {
        name: "Graphic Design",
        level: 85,
        desc: "Branding, poster, konten media sosial, dan materi promosi dengan Canva.",
      },
      {
        name: "Microsoft Office",
        level: 80,
        desc: "Pengolahan dokumen, presentasi, dan data dengan Word, PowerPoint, dan Excel.",
      },
    ],
    servicesEyebrow: "Layanan",
    servicesTitle: "Services",
    services: [
      {
        title: "Desain Grafis",
        desc: "Poster, banner, logo sederhana, dan materi branding visual untuk kebutuhan digital maupun cetak.",
      },
      {
        title: "Konten Media Sosial",
        desc: "Desain feed, story, dan konten promosi yang menarik dan sesuai identitas brand Anda.",
      },
      {
        title: "Presentasi & Dokumen",
        desc: "Slide presentasi profesional, proposal, dan penataan dokumen yang rapi dan komunikatif.",
      },
      {
        title: "Konten Promosi",
        desc: "Materi promosi produk dan acara yang mengikuti tren visual modern agar pesan tersampaikan.",
      },
    ],
    portfolioEyebrow: "Karya Saya",
    portfolioTitle: "Portfolio",
    portfolioEmpty: "Belum ada proyek pada kategori ini. Nantikan karya berikutnya!",
    experienceEyebrow: "Perjalanan",
    experienceTitle: "Experience & Education",
    timeline: [
      {
        period: "Sekarang",
        title: "Mahasiswa Kewirausahaan",
        place: "Universitas Yatsi Madani",
        desc: "Menempuh pendidikan tinggi sambil mengembangkan kemampuan desain visual dan kewirausahaan.",
      },
      {
        period: "Freelance",
        title: "Desainer Grafis",
        place: "Canva Design",
        desc: "Mengerjakan desain poster, konten media sosial, dan materi promosi untuk berbagai kebutuhan.",
      },
      {
        period: "Pengalaman Kerja",
        title: "Driver ShopeeFood",
        place: "ShopeeFood",
        desc: "Melatih manajemen waktu, pelayanan pelanggan, dan tanggung jawab dalam pekerjaan lapangan.",
      },
      {
        period: "Pengalaman Kerja",
        title: "Helper Tukang",
        place: "Proyek Konstruksi",
        desc: "Membangun etos kerja keras, kedisiplinan, dan kemampuan bekerja dalam tim.",
      },
    ],
    testimonialsEyebrow: "Apa Kata Mereka",
    testimonialsTitle: "Testimonials",
    testimonials: [
      {
        name: "Panitia KKN",
        role: "Universitas Yatsi Madani",
        text: "Desain poster kegiatan KKN dibuat dengan cepat dan hasilnya menarik. Komunikasi selama pengerjaan juga sangat baik.",
      },
      {
        name: "Teman Kuliah",
        role: "Universitas Yatsi Madani",
        text: "Ardi sering membantu membuat desain presentasi dan poster tugas. Hasilnya rapi, modern, dan selalu selesai tepat waktu.",
      },
      {
        name: "Klien Desain",
        role: "Konten Media Sosial",
        text: "Konten promosi yang didesain Ardi membuat tampilan media sosial kami jauh lebih profesional dan menarik perhatian.",
      },
    ],
    contactEyebrow: "Hubungi Saya",
    contactTitle: "Contact",
    contactLead:
      "Tertarik bekerja sama atau butuh jasa desain? Kirim pesan — pesan Anda akan langsung diteruskan ke WhatsApp saya.",
    formName: "Nama",
    formNamePlaceholder: "Nama Anda",
    formMessage: "Pesan",
    formMessagePlaceholder: "Tulis kebutuhan desain atau pertanyaan Anda...",
    formSubmit: "Kirim via WhatsApp",
    waMessage: (name, email, message) =>
      `Halo Ardi, saya ${name} (${email}).\n\n${message}`,
    waFloating: "Halo Ardi, saya tertarik dengan jasa desain Anda.",
    footerTagline: "Desain visual yang menarik, komunikatif, dan modern.",
    closeLabel: "Tutup",
  },
  en: {
    themeLabel: "Toggle theme",
    menuLabel: "Open menu",
    signIn: "Sign in",
    memberEyebrow: "Exclusive Area",
    memberTitle: "Member Area",
    memberLead: "The full catalog of raw design files in full resolution — exclusively for signed-in users.",
    memberLockedTitle: "Members Only",
    memberLockedText: "Sign in with your Google account to access and download all full-resolution design files.",
    memberDownload: "Download",
    heroGreeting: "Hi, I'm Ardi",
    typing: ["Graphic Designer", "Entrepreneurship Student"],
    heroDesc:
      "As a creative and experienced entrepreneurship student, I create visual designs for both digital and print needs — branding, social media, presentations, posters, and promotional content. I focus on designs that are engaging, communicative, and true to a brand's identity, while always keeping up with modern visual trends.",
    viewWork: "View My Work",
    contactMe: "Contact Me",
    downloadCv: "Download CV",
    aboutEyebrow: "About Me",
    aboutTitle: "About Me",
    aboutDesc:
      "I am a university student with hands-on experience as a construction helper and a ShopeeFood driver, and I also design with Canva. Field work taught me discipline, responsibility, and communication — while design is where I create and help brands look their best.",
    education: "Student at Yatsi Madani University",
    skillsEyebrow: "My Skills",
    skillsTitle: "Skills",
    skills: [
      {
        name: "Graphic Design",
        level: 85,
        desc: "Branding, posters, social media content, and promotional materials with Canva.",
      },
      {
        name: "Microsoft Office",
        level: 80,
        desc: "Documents, presentations, and data processing with Word, PowerPoint, and Excel.",
      },
    ],
    servicesEyebrow: "What I Offer",
    servicesTitle: "Services",
    services: [
      {
        title: "Graphic Design",
        desc: "Posters, banners, simple logos, and visual branding materials for digital and print needs.",
      },
      {
        title: "Social Media Content",
        desc: "Engaging feed, story, and promotional content designs that match your brand identity.",
      },
      {
        title: "Presentations & Documents",
        desc: "Professional slide decks, proposals, and clean, communicative document layouts.",
      },
      {
        title: "Promotional Content",
        desc: "Product and event promo materials that follow modern visual trends so your message lands.",
      },
    ],
    portfolioEyebrow: "My Work",
    portfolioTitle: "Portfolio",
    portfolioEmpty: "No projects in this category yet. Stay tuned for more!",
    experienceEyebrow: "My Journey",
    experienceTitle: "Experience & Education",
    timeline: [
      {
        period: "Present",
        title: "Entrepreneurship Student",
        place: "Yatsi Madani University",
        desc: "Pursuing higher education while developing visual design and entrepreneurship skills.",
      },
      {
        period: "Freelance",
        title: "Graphic Designer",
        place: "Canva Design",
        desc: "Creating posters, social media content, and promotional materials for various needs.",
      },
      {
        period: "Work Experience",
        title: "ShopeeFood Driver",
        place: "ShopeeFood",
        desc: "Trained time management, customer service, and responsibility in field work.",
      },
      {
        period: "Work Experience",
        title: "Construction Helper",
        place: "Construction Projects",
        desc: "Built a strong work ethic, discipline, and the ability to work in a team.",
      },
    ],
    testimonialsEyebrow: "What They Say",
    testimonialsTitle: "Testimonials",
    testimonials: [
      {
        name: "KKN Committee",
        role: "Yatsi Madani University",
        text: "The KKN event poster was made quickly and the result is engaging. Communication throughout the work was excellent.",
      },
      {
        name: "College Friend",
        role: "Yatsi Madani University",
        text: "Ardi often helps design presentations and assignment posters. The results are neat, modern, and always finished on time.",
      },
      {
        name: "Design Client",
        role: "Social Media Content",
        text: "The promotional content Ardi designed made our social media look far more professional and eye-catching.",
      },
    ],
    contactEyebrow: "Get In Touch",
    contactTitle: "Contact",
    contactLead:
      "Interested in working together or need design services? Send a message — it will be forwarded straight to my WhatsApp.",
    formName: "Name",
    formNamePlaceholder: "Your name",
    formMessage: "Message",
    formMessagePlaceholder: "Tell me about your design needs or questions...",
    formSubmit: "Send via WhatsApp",
    waMessage: (name, email, message) =>
      `Hi Ardi, I'm ${name} (${email}).\n\n${message}`,
    waFloating: "Hi Ardi, I'm interested in your design services.",
    footerTagline: "Engaging, communicative, and modern visual design.",
    closeLabel: "Close",
  },
};

function useReveal() {
  useEffect(() => {
    const observed = new WeakSet();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    const observeNewElements = () => {
      document.querySelectorAll(".reveal:not(.revealed)").forEach((el) => {
        if (observed.has(el)) return;
        observed.add(el);
        observer.observe(el);
      });
    };
    observeNewElements();
    // Pantau elemen .reveal yang ditambahkan kemudian (ganti filter portofolio, kartu member setelah login, dll)
    const mutationObserver = new MutationObserver(observeNewElements);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
}

function useTyping(words) {
  const [text, setText] = useState("");
  const [wordIndex, setWordIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setText("");
    setWordIndex(0);
    setDeleting(false);
  }, [words]);

  useEffect(() => {
    const word = words[wordIndex % words.length];
    const speed = deleting ? 45 : 90;
    const timer = setTimeout(() => {
      if (!deleting) {
        const next = word.slice(0, text.length + 1);
        setText(next);
        if (next === word) setTimeout(() => setDeleting(true), 1400);
      } else {
        const next = word.slice(0, text.length - 1);
        setText(next);
        if (next === "") {
          setDeleting(false);
          setWordIndex((i) => (i + 1) % words.length);
        }
      }
    }, speed);
    return () => clearTimeout(timer);
  }, [text, deleting, wordIndex, words]);

  return text;
}

const scrollToSection = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

function Navbar({ theme, onToggleTheme, lang, onToggleLang, t, user, onLogout }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header className={`navbar ${scrolled ? "navbar-scrolled" : ""}`} data-testid="navbar">
      <div className="navbar-inner">
        <a
          href="#about"
          className="navbar-logo"
          data-testid="navbar-logo"
          onClick={(e) => handleNav(e, "#about")}
        >
          Ardi<span>.</span>
        </a>
        <nav className={`navbar-links ${open ? "open" : ""}`} data-testid="navbar-menu">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              onClick={(e) => handleNav(e, link.href)}
            >
              {link.label}
            </a>
          ))}
        </nav>
        <div className="navbar-actions">
          {user ? (
            <div className="user-chip" data-testid="user-menu">
              {user.picture && (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="user-avatar"
                  data-testid="user-avatar"
                />
              )}
              <span className="user-name" data-testid="user-name">
                {user.name}
              </span>
              <button
                className="icon-btn"
                onClick={onLogout}
                aria-label="Keluar / Log out"
                data-testid="logout-button"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <button
              className="icon-btn lang-btn"
              onClick={handleGoogleSignIn}
              data-testid="google-signin-button"
            >
              <LogIn size={16} />
              <span>{t.signIn}</span>
            </button>
          )}
          <button
            className="icon-btn lang-btn"
            onClick={onToggleLang}
            aria-label="Ganti bahasa / Switch language"
            data-testid="language-toggle-button"
          >
            <Languages size={16} />
            <span>{lang === "id" ? "EN" : "ID"}</span>
          </button>
          <button
            className="icon-btn"
            onClick={onToggleTheme}
            aria-label={t.themeLabel}
            data-testid="theme-toggle-button"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            className="icon-btn hamburger"
            onClick={() => setOpen((v) => !v)}
            aria-label={t.menuLabel}
            data-testid="hamburger-menu-button"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
    </header>
  );
}

function About({ t }) {
  const typed = useTyping(t.typing);
  return (
    <section id="about" className="section section-first" data-testid="about-section">
      <div className="section-container">
        <p className="section-eyebrow">{t.aboutEyebrow}</p>
        <h2 className="section-title reveal">{t.aboutTitle}</h2>
        <div className="about-grid">
          <div className="about-photo-wrap reveal">
            <img src={PROFILE_PHOTO} alt="Ardi Septiono" data-testid="about-photo" />
          </div>
          <div className="about-text reveal">
            <p className="hero-typing" data-testid="about-typing-text">
              {typed}
              <span className="cursor">|</span>
            </p>
            <p data-testid="about-description">{t.aboutDesc}</p>
            <div className="about-info">
              <div className="info-item" data-testid="about-info-location">
                <MapPin size={16} />
                <span>Rajeg, Kabupaten Tangerang</span>
              </div>
              <div className="info-item" data-testid="about-info-email">
                <Mail size={16} />
                <span>ardiseptiono80@gmail.com</span>
              </div>
              <div className="info-item" data-testid="about-info-education">
                <GraduationCap size={16} />
                <span>{t.education}</span>
              </div>
            </div>
            <div className="hero-buttons">
              <button
                className="btn btn-primary"
                data-testid="view-my-work-button"
                onClick={() => scrollToSection("#portfolio")}
              >
                {t.viewWork}
              </button>
              <button
                className="btn btn-outline"
                data-testid="contact-me-button"
                onClick={() => scrollToSection("#contact")}
              >
                {t.contactMe}
              </button>
              <a
                className="btn btn-ghost"
                href={CV_URL}
                download="CV-Ardi-Septiono.pdf"
                data-testid="download-cv-button"
              >
                <Download size={16} /> {t.downloadCv}
              </a>
            </div>
            <div className="hero-socials" data-testid="about-social-links">
              {SOCIALS.map(({ name, href, Icon }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  data-testid={`social-link-${name.toLowerCase()}`}
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Skills({ t }) {
  const icons = [Palette, FileText];
  return (
    <section id="skills" className="section section-alt" data-testid="skills-section">
      <div className="section-container">
        <p className="section-eyebrow">{t.skillsEyebrow}</p>
        <h2 className="section-title reveal">{t.skillsTitle}</h2>
        <div className="skills-grid">
          {t.skills.map(({ name, level, desc }, i) => {
            const Icon = icons[i];
            return (
              <div
                className="skill-card reveal"
                key={name}
                data-testid={`skill-card-${name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="skill-icon">
                  <Icon size={26} />
                </div>
                <h3>{name}</h3>
                <p>{desc}</p>
                <div className="skill-bar">
                  <div className="skill-bar-fill" style={{ width: `${level}%` }} />
                </div>
                <span className="skill-level">{level}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Services({ t }) {
  const icons = [Palette, Megaphone, Presentation, Award];
  return (
    <section id="services" className="section" data-testid="services-section">
      <div className="section-container">
        <p className="section-eyebrow">{t.servicesEyebrow}</p>
        <h2 className="section-title reveal">{t.servicesTitle}</h2>
        <div className="services-grid">
          {t.services.map(({ title, desc }, i) => {
            const Icon = icons[i];
            return (
              <div
                className="service-card reveal"
                key={title}
                data-testid={`service-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="service-icon">
                  <Icon size={26} />
                </div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Portfolio({ onOpenProject, lang, t }) {
  const [filter, setFilter] = useState("all");
  const visible = PROJECTS.filter((p) => filter === "all" || p.category === filter);

  return (
    <section id="portfolio" className="section section-alt" data-testid="portfolio-section">
      <div className="section-container">
        <p className="section-eyebrow">{t.portfolioEyebrow}</p>
        <h2 className="section-title reveal">{t.portfolioTitle}</h2>
        <div className="portfolio-filters" data-testid="portfolio-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`filter-btn ${filter === f.key ? "active" : ""}`}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}-button`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {visible.length === 0 ? (
          <div className="portfolio-empty" data-testid="portfolio-empty-state">
            <FileText size={36} />
            <p>{t.portfolioEmpty}</p>
          </div>
        ) : (
          <div className="portfolio-grid" data-testid="portfolio-grid">
            {visible.map((project) => (
              <button
                key={project.id}
                className="portfolio-card reveal"
                onClick={() => onOpenProject(project)}
                data-testid={`project-card-${project.id}`}
              >
                {project.type === "video" ? (
                  <>
                    <video
                      src={project.src}
                      poster={project.poster}
                      muted
                      playsInline
                      loop
                      preload="metadata"
                    />
                    <span className="video-badge">
                      <Play size={20} />
                    </span>
                  </>
                ) : (
                  <img src={project.src} alt={project.title[lang]} loading="lazy" />
                )}
                <div className="portfolio-overlay">
                  <span className="portfolio-category">{project.categoryLabel}</span>
                  <h3>{project.title[lang]}</h3>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ProjectModal({ project, onClose, lang, t }) {
  useEffect(() => {
    if (!project) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [project, onClose]);

  if (!project) return null;
  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="project-modal">
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <button
          className="modal-close"
          onClick={onClose}
          aria-label={t.closeLabel}
          data-testid="modal-close-button"
        >
          <X size={20} />
        </button>
        {project.type === "video" ? (
          <video
            src={project.src}
            poster={project.poster}
            controls
            autoPlay
            playsInline
            data-testid="modal-project-video"
          />
        ) : (
          <img src={project.src} alt={project.title[lang]} data-testid="modal-project-image" />
        )}
        <div className="modal-body">
          <span className="portfolio-category" data-testid="modal-project-category">
            {project.categoryLabel}
          </span>
          <h3 data-testid="modal-project-title">{project.title[lang]}</h3>
          <p data-testid="modal-project-description">{project.description[lang]}</p>
        </div>
      </div>
    </div>
  );
}

function Experience({ t }) {
  const icons = [GraduationCap, Palette, Briefcase, Briefcase];
  return (
    <section id="experience" className="section" data-testid="experience-section">
      <div className="section-container">
        <p className="section-eyebrow">{t.experienceEyebrow}</p>
        <h2 className="section-title reveal">{t.experienceTitle}</h2>
        <div className="timeline">
          {t.timeline.map(({ period, title, place, desc }, i) => {
            const Icon = icons[i];
            return (
              <div className="timeline-item reveal" key={title} data-testid={`timeline-item-${i}`}>
                <div className="timeline-dot">
                  <Icon size={18} />
                </div>
                <div className="timeline-content">
                  <span className="timeline-period">{period}</span>
                  <h3>{title}</h3>
                  <span className="timeline-place">{place}</span>
                  <p>{desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Testimonials({ t }) {
  return (
    <section id="testimonials" className="section section-alt" data-testid="testimonials-section">
      <div className="section-container">
        <p className="section-eyebrow">{t.testimonialsEyebrow}</p>
        <h2 className="section-title reveal">{t.testimonialsTitle}</h2>
        <div className="testimonials-grid">
          {t.testimonials.map((item, i) => (
            <div
              className="testimonial-card reveal"
              key={item.name}
              data-testid={`testimonial-card-${i}`}
            >
              <p className="testimonial-text">"{item.text}"</p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">{item.name.charAt(0)}</div>
                <div>
                  <strong>{item.name}</strong>
                  <span>{item.role}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MemberArea({ user, t }) {
  const [files, setFiles] = useState(null);

  useEffect(() => {
    if (!user) {
      setFiles(null);
      return;
    }
    fetch(`${API}/member/files`, { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setFiles(data.files))
      .catch(() => {});
  }, [user]);

  return (
    <section id="member" className="section" data-testid="member-section">
      <div className="section-container">
        <p className="section-eyebrow">{t.memberEyebrow}</p>
        <h2 className="section-title reveal">{t.memberTitle}</h2>
        {!user ? (
          <div className="member-locked reveal" data-testid="member-locked">
            <Lock size={36} />
            <h3>{t.memberLockedTitle}</h3>
            <p>{t.memberLockedText}</p>
            <button
              className="btn btn-primary"
              onClick={handleGoogleSignIn}
              data-testid="member-signin-button"
            >
              <LogIn size={16} /> {t.signIn}
            </button>
          </div>
        ) : (
          <>
            <p className="member-lead" data-testid="member-lead">
              {t.memberLead}
            </p>
            <div className="member-grid" data-testid="member-grid">
              {(files || []).map((f) => (
                <div className="member-card reveal" key={f.id} data-testid={`member-file-card-${f.id}`}>
                  {f.type === "video" ? (
                    <video src={f.url} muted playsInline preload="metadata" />
                  ) : (
                    <img src={f.url} alt={f.title} loading="lazy" />
                  )}
                  <h3>{f.title}</h3>
                  <a
                    className="btn btn-ghost"
                    href={f.url}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`member-download-${f.id}`}
                  >
                    <Download size={16} /> {t.memberDownload}
                  </a>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function Contact({ t }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = t.waMessage(form.name, form.email, form.message);
    window.open(`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <section id="contact" className="section" data-testid="contact-section">
      <div className="section-container">
        <p className="section-eyebrow">{t.contactEyebrow}</p>
        <h2 className="section-title reveal">{t.contactTitle}</h2>
        <div className="contact-grid">
          <div className="contact-info reveal">
            <p className="contact-lead">{t.contactLead}</p>
            <div className="info-item" data-testid="contact-email">
              <Mail size={16} />
              <a href="mailto:ardiseptiono80@gmail.com">ardiseptiono80@gmail.com</a>
            </div>
            <div className="info-item" data-testid="contact-whatsapp">
              <MessageCircle size={16} />
              <a href={`https://wa.me/${WA_NUMBER}`} target="_blank" rel="noopener noreferrer">
                0821-1879-0441
              </a>
            </div>
            <div className="info-item" data-testid="contact-location">
              <MapPin size={16} />
              <span>Rajeg, Kabupaten Tangerang</span>
            </div>
          </div>
          <form className="contact-form reveal" onSubmit={handleSubmit} data-testid="contact-form">
            <label>
              {t.formName}
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t.formNamePlaceholder}
                data-testid="contact-name-input"
              />
            </label>
            <label>
              Email
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@anda.com"
                data-testid="contact-email-input"
              />
            </label>
            <label>
              {t.formMessage}
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t.formMessagePlaceholder}
                data-testid="contact-message-input"
              />
            </label>
            <button type="submit" className="btn btn-primary" data-testid="contact-submit-button">
              <MessageCircle size={16} /> {t.formSubmit}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Footer({ t }) {
  return (
    <footer className="footer" data-testid="footer">
      <div className="section-container footer-inner">
        <div>
          <span className="navbar-logo">
            Ardi<span>.</span>
          </span>
          <p className="footer-tagline">{t.footerTagline}</p>
        </div>
        <div className="footer-socials">
          {SOCIALS.map(({ name, href, Icon }) => (
            <a
              key={name}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={name}
              data-testid={`footer-social-${name.toLowerCase()}`}
            >
              <Icon size={18} />
            </a>
          ))}
        </div>
        <p className="footer-copy" data-testid="footer-copyright">
          © {new Date().getFullYear()} Ardi Septiono. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

function FloatingButtons({ t }) {
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 500);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <a
        href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(t.waFloating)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="floating-wa"
        aria-label="Chat WhatsApp"
        data-testid="whatsapp-floating-button"
      >
        <MessageCircle size={24} />
      </a>
      {showTop && (
        <button
          className="back-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Kembali ke atas / Back to top"
          data-testid="back-to-top-button"
        >
          <ArrowUp size={20} />
        </button>
      )}
    </>
  );
}

function AuthCallback() {
  const hasProcessed = useRef(false);
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
    fetch(`${API}/auth/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ session_id: sessionId }),
    })
      .catch(() => {})
      .finally(() => {
        window.location.replace(window.location.origin + "/");
      });
  }, []);
  return null;
}

function AppShell() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return <PortfolioApp />;
}

function PortfolioApp() {
  const [theme, setTheme] = useState("dark");
  const [lang, setLang] = useState("id");
  const [activeProject, setActiveProject] = useState(null);
  const [user, setUser] = useState(null);

  useReveal();

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  useEffect(() => {
    // CRITICAL: saat kembali dari OAuth callback, lewati cek /me — AuthCallback yang menukar session_id lebih dulu
    if (window.location.hash?.includes("session_id=")) return;
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setUser(data))
      .catch(() => {});
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  const toggleLang = useCallback(() => {
    setLang((l) => (l === "id" ? "en" : "id"));
  }, []);

  const handleLogout = useCallback(() => {
    fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" })
      .catch(() => {})
      .finally(() => setUser(null));
  }, []);

  const t = CONTENT[lang];

  return (
    <div className="App">
      <Navbar
        theme={theme}
        onToggleTheme={toggleTheme}
        lang={lang}
        onToggleLang={toggleLang}
        t={t}
        user={user}
        onLogout={handleLogout}
      />
      <main>
        <About t={t} />
        <Skills t={t} />
        <Services t={t} />
        <Portfolio onOpenProject={setActiveProject} lang={lang} t={t} />
        <MemberArea user={user} t={t} />
        <Experience t={t} />
        <Contact t={t} />
      </main>
      <Footer t={t} />
      <FloatingButtons t={t} />
      <ProjectModal
        project={activeProject}
        onClose={() => setActiveProject(null)}
        lang={lang}
        t={t}
      />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
